import {formatDistanceToNow} from 'date-fns';
import {Check, Pencil, RotateCw, X} from 'lucide-react';
import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {statusBadge} from './alertStatus';
import {CopyUrlButton} from '@/components/shared/CopyableUrl';
import {type P0Alert} from '@/api/p0Alerts';
import {type Submission} from '@/api/submissions';
import {EmailPreview} from '@/components/shared/RichTextArea';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {TruncatedText} from '@/components/shared/TruncatedText';
import {PlatformStatusCell} from '@/components/submissions/SubmissionsTable';
import {SubmissionStatusPill} from '@/components/submissions/SubmissionStatusPill';
import {enforcementActions} from '@/lib/enforcement';
import {ACTOR_LABEL, CONTENT_LABEL} from '@/lib/platform';

function SubmissionLine({sub}: {readonly sub: Submission}) {
  const contentId = sub.item_external_id ?? sub.content_id;
  const name = sub.display_name ?? sub.handle ?? contentId;
  // A delivered submission shows what the platform did about it; a PRI-side state (escalated,
  // removed by QC) is not an enforcement action, so it stays the delivery status.
  const actions = sub.action_status === 'sent to client' ? enforcementActions(sub) : [];

  return (
    <li className="border-border flex gap-3 rounded-lg border p-3">
      <div className="bg-muted size-14 shrink-0 overflow-hidden rounded-md">
        {sub.thumbnail_url ? (
          <SensitiveMedia
            compact
            src={sub.thumbnail_url}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary">
            {sub.submission_type === 'Actor' ? ACTOR_LABEL : CONTENT_LABEL}
          </Badge>
          <TruncatedText className="font-mono text-xs font-medium">{contentId}</TruncatedText>
          {sub.source_url ? <CopyUrlButton url={sub.source_url} /> : null}
        </div>
        <TruncatedText className="text-xs">{name}</TruncatedText>
        <div className="flex flex-wrap items-center gap-1.5">
          {actions.length > 0 ? (
            actions.map((action) => (
              <Badge key={action} variant="destructive">
                {action}
              </Badge>
            ))
          ) : (
            <SubmissionStatusPill status={sub.action_status} />
          )}
        </div>
        <PlatformStatusCell sub={sub} />
        <p className="text-muted-foreground text-xs">
          {sub.policy_violation} · {sub.risks.join(', ') || '—'}
        </p>
      </div>
    </li>
  );
}

// The P0 page's detail drawer, the counterpart to Submissions' SubmissionDetailSheet: the alert's
// status and who handled it, the email as it goes out, and every submission it covers with what the
// platform did about each. Decisions are made here or from the row's menu.
export function P0AlertDetailSheet({
  alert,
  canDecide,
  acting,
  onOpenChange,
  onApprove,
  onAmend,
  onReject,
  onRetry
}: {
  readonly alert: P0Alert | null;
  readonly canDecide: boolean;
  readonly acting: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onApprove: (alert: P0Alert) => void;
  readonly onAmend: (alert: P0Alert) => void;
  readonly onReject: (alert: P0Alert) => void;
  readonly onRetry: (alert: P0Alert) => void;
}) {
  return (
    <Sheet open={alert !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 data-[side=right]:sm:max-w-xl">
        {alert ? (
          <>
            <SheetHeader className="gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant={statusBadge(alert.status).variant}>
                  {statusBadge(alert.status).label}
                </Badge>
                {[...new Set(alert.submissions.map((s) => s.policy_violation))].map((pv) => (
                  <Badge key={pv} variant="outline">
                    {pv}
                  </Badge>
                ))}
              </div>
              <SheetTitle className="break-words">
                {alert.subject ?? 'Untitled P0 Alert'}
              </SheetTitle>
              <SheetDescription>
                Raised by {alert.created_by}{' '}
                {formatDistanceToNow(new Date(alert.created_at), {addSuffix: true})}
                {alert.decided_by ? `, handled by ${alert.decided_by}` : ''}
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {alert.decision_reason ? (
                <p className="text-destructive-ink text-xs">{alert.decision_reason}</p>
              ) : null}
              {alert.delivery_error ? (
                <p className="text-destructive-ink text-xs">{alert.delivery_error}</p>
              ) : null}

              <section className="space-y-2">
                <h3 className="text-sm font-medium">
                  Submissions ({alert.submissions.length.toLocaleString()})
                </h3>
                <ul className="space-y-2">
                  {alert.submissions.map((sub) => (
                    <SubmissionLine key={sub.id} sub={sub} />
                  ))}
                </ul>
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-medium">Email</h3>
                <EmailPreview subject={alert.subject ?? ''} bodyHtml={alert.notes ?? ''} />
              </section>
            </div>

            {canDecide && (alert.status === 'pending_approval' || alert.status === 'approved') ? (
              <SheetFooter className="flex-row justify-end gap-2">
                {alert.status === 'pending_approval' ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={acting}
                      onClick={() => {
                        onApprove(alert);
                      }}>
                      {acting ? <Spinner /> : <Check />}
                      Approve
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={acting}
                      onClick={() => {
                        onAmend(alert);
                      }}>
                      <Pencil />
                      Amend
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={acting}
                      onClick={() => {
                        onReject(alert);
                      }}>
                      <X />
                      Reject
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={acting}
                    onClick={() => {
                      onRetry(alert);
                    }}>
                    {acting ? <Spinner /> : <RotateCw />}
                    Retry Delivery
                  </Button>
                )}
              </SheetFooter>
            ) : null}
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
